import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { renderUrl, type DashboardTarget } from '../../../lib/dashboard';
import { SECTION_PATH, NavSection, type NavDestination } from '../../../lib/pages';
import { useBasePath } from '../../../lib/use-base-path';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { callWidgetTool } from '../../../lib/widgets';
import { API_BASE } from '../../../lib/api-base';
import { useTranslation } from '../../../lib/i18n';
import { useConfirm } from '../../ConfirmDialog';
import { SYM_MARKER } from './constants';
import { useTheme } from '../../../lib/theme-context';

type WidgetMessage = {
  __symWidget?: string;
  id?: string;
  kind?: string;
  tool?: string;
  params?: unknown;
  prompt?: string;
  context?: string;
  title?: string;
  message?: string;
  // The failing widget's slug, structured. It is ALSO prefixed into `message` for the banner, but a
  // telemetry row keyed off a display string stops matching reality the moment that copy changes.
  slug?: string;
  destination?: NavDestination;
  height?: number;
};

export function useDashboardFrame({
  target,
  reloadKey,
  workspace,
  docHeight,
  onHeight,
}: {
  target: DashboardTarget;
  reloadKey: number;
  workspace?: string;
  docHeight: number | null;
  onHeight: (height: number) => void;
}) {
  const router = useRouter();
  const basePath = useBasePath();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  // The `message` listener below is registered ONCE (its effect depends only on `addError`), so anything
  // it reads from render scope is frozen at first render. `target` changes as the reader moves between
  // dashboards and drill-in pages without remounting, so the failure report reads it through a ref —
  // otherwise every row after the first navigation would name the dashboard the reader STARTED on, and a
  // mis-attributed row is worse than a missing one because it looks like data.
  const targetRef = useRef(target);
  targetRef.current = target;
  const addError = useCallback((line: string) => setErrors((prev) => (prev.includes(line) ? prev : [...prev, line])), []);
  const [contentReady, setContentReady] = useState(false);

  const src = useMemo(() => renderUrl(target, reloadKey), [target.dashboard, target.page, reloadKey]);

  // The dashboard document is its own — the host's `data-theme` cannot reach it through the cascade,
  // so it is TOLD which of its two palettes to wear, exactly as the phones tell it
  // (`PageWebView.deliverTheme`). This posted a hardcoded `'light'`, which is why a dark home and a
  // dark workspace still showed white dashboard cards. See the fuller note in PageSurface.tsx.
  const { theme } = useTheme();
  const postTheme = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({ __symWidget: SYM_MARKER, kind: 'theme', theme }, '*');
  }, [theme]);

  // A theme sent before the document is ready is dropped, so it goes on load AND on every flip.
  // No reload — the runtime recolours in place.
  useEffect(() => {
    postTheme();
  }, [postTheme]);

  const onIframeLoad = useCallback(() => {
    postTheme();
    requestAnimationFrame(() => setContentReady(true));
  }, [postTheme]);

  const prevSrcRef = useRef(src);
  if (prevSrcRef.current !== src) {
    prevSrcRef.current = src;
    setContentReady(false);
  }

  const navRef = useRef({ router, basePath, workspace, onHeight, t, confirm });
  navRef.current = { router, basePath, workspace, onHeight, t, confirm };
  const onMessage = (e: MessageEvent) => {
    const iframe = iframeRef.current;
    if (!iframe || e.source !== iframe.contentWindow) return;
    const d = e.data as WidgetMessage;
    if (!d || d.__symWidget !== SYM_MARKER) return;
    const reply = (payload: object) => iframe.contentWindow?.postMessage({ __symWidget: SYM_MARKER, ...payload }, '*');
    const { router: r, basePath: bp, workspace: ws, onHeight: reportHeight, t: translate, confirm: ask } = navRef.current;

    if (d.kind === 'call' && d.id) {
      callWidgetTool(d.tool || '', d.params)
        .then((result) => reply({ id: d.id, ok: true, result }))
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : String(err);
          reply({ id: d.id, ok: false, error: message });
          // A failed tool call is not a page error — a widget probing data absent for this account fails
          // routinely and then shows its own empty/error state. The banner is raised only when a widget
          // actually fails to render, via the runtime's `kind: 'error'` message (below). Reply, don't banner.
        });
    } else if (d.kind === 'confirm' && d.id) {
      // Same shape as the phones' native alert: the widget's title (or "Are you sure?"), its message, Confirm.
      void ask({
        title: d.title || translate('common.areYouSure'),
        message: d.message,
        confirmLabel: translate('common.confirm'),
        bi: { page_name: 'dashboard', modal_name: 'widget_confirm' },
      }).then((ok) => reply({ id: d.id, ok: true, result: ok }));
    } else if (d.kind === 'chat') {
      const text = [d.prompt, d.context].filter(Boolean).join('\n\n');
      const link = chatHandoffLink({ kind: 'new', message: text }, bp);
      void r.push(link.route, link.path);
    } else if (d.kind === 'navigate' && d.destination) {
      const destination = d.destination;
      if ('workspace' in destination) {
        void r.push(
          { pathname: '/workspace/[slug]', query: { slug: destination.workspace } },
          `${bp}/workspace/${encodeURIComponent(destination.workspace)}`,
        );
      } else if ('page' in destination) {
        if (ws) {
          void r.push(
            { pathname: '/workspace/[slug]/pages/[page]', query: { slug: ws, page: destination.page } },
            `${bp}/workspace/${encodeURIComponent(ws)}/pages/${encodeURIComponent(destination.page)}`,
          );
        } else {
          void r.push({ pathname: '/pages/[slug]', query: { slug: destination.page } }, `${bp}/pages/${encodeURIComponent(destination.page)}`);
        }
      } else {
        // A destination shape this host does not know must NOT move the user. `SECTION_PATH[undefined]`
        // fell through to Home, so a library widget emitting `{app: …}` (appointment-detail's "Go to
        // Booking Calendar" does) threw the reader out of the page they were reading, silently. Losing
        // your place is worse than a tap that does nothing, and a dead tap is at least diagnosable.
        const section = (destination as { section?: string }).section;
        if (section && section in SECTION_PATH) {
          void r.push(`${bp}${SECTION_PATH[section as NavSection]}`); // a built-in section is another screen
        } else {
          console.warn('[dashboard-nav] ignored a destination this host does not handle', destination);
        }
      }
    } else if (d.kind === 'error') {
      // Raised only by the runtime binder when a widget genuinely fails to render (tagged with its slug);
      // the runtime no longer forwards raw window errors, so this is a real, actionable widget failure.
      // Logged as well as collected — see the twin comment in `PageSurface.tsx`: with the banner
      // classifying, a page whose only failures are a missing connection or a rate limit offers no
      // "Copy details", so the log is where the raw diagnostic stays readable.
      console.warn('dashboard: widget runtime error', d.message);
      // AND server-side. This surface renders every dashboard that is NOT the home one (a workspace's
      // dashboard and every drill-in page), so a beacon wired only into `PageSurface` would have given
      // the metric a denominator of "home dashboards on web" while both phones reported everything: the
      // shape of split that inverts a conclusion. Same fire-and-forget contract as the twin there —
      // `API_BASE` for the active mount, plain `fetch` so an unauthenticated instance cannot redirect a
      // reader away from the page they are looking at, `keepalive` so it survives their leaving.
      void fetch(`${API_BASE}/api/widget-runtime-error`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          widgetSlug: d.slug ?? '',
          message: d.message ?? '',
          dashboardSlug: targetRef.current.page || targetRef.current.dashboard || '',
        }),
        keepalive: true,
      }).catch(() => undefined);
      addError(d.message || translate('dashboard.pageError'));
    } else if (d.kind === 'errorCleared') {
      // The recovery half, which this surface never had. `sym.clearError` fires when the LAST widget in
      // the error state repaints, and the banner is held in host state where the document cannot reach
      // it — so without this the banner was write-only here, and one transient failure left "didn't load
      // correctly" up indefinitely accusing a dashboard that had already recovered. That was fixed in
      // `PageSurface` and on both phones, and the parity test pinned exactly those three, which is why
      // web's OTHER dashboard surface kept the bug in plain sight.
      setErrors([]);
    } else if (d.kind === 'height' && typeof d.height === 'number') {
      reportHeight(d.height);
    }
  };
  useEffect(() => {
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [addError]);

  useEffect(() => {
    if (docHeight == null) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    const scroller = iframe.closest('.orion-content-pane') as HTMLElement | null;
    let raf = 0;
    const push = () => {
      raf = 0;
      const ifr = iframeRef.current;
      if (!ifr || !ifr.contentWindow) return;
      const ir = ifr.getBoundingClientRect();
      const sr = (scroller ?? document.documentElement).getBoundingClientRect();
      const top = Math.max(0, sr.top - ir.top);
      const h = Math.max(0, Math.min(ir.height, sr.bottom - ir.top) - top);
      ifr.contentWindow.postMessage({ __symWidget: SYM_MARKER, kind: 'viewport', top, h }, '*');
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(push);
    };
    scroller?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    push();
    return () => {
      scroller?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [src, docHeight]);

  useEffect(() => setErrors([]), [src]);

  return { iframeRef, src, basePath, errors, contentReady, onIframeLoad };
}
